import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import { twMerge } from 'tailwind-merge';
import Progress from 'components/Progress/Progress';
import ProgressBar from 'components/ProgressBar/ProgressBar';

import Logo from 'assets/logo.svg?react';
import LogoFooter from 'assets/logo-muted.svg?react';
import LogoSmall from 'assets/logo-small.svg?react';
import HelpIcon from 'assets/icons/calculator-help.svg?react';

import styles from './Layout.module.scss';
import SidebarAmountAndMTP from './SidebarAmountAndMTP';
import Drawer from './Drawer';
import useCalculator from 'hooks/calculator/useCalculator';
import MobileCalculalorChevron from './MobileCalculatorChevron';
import DesktopCalculatorButton from './DesktopCalculatorButton';

export type Props = {
  showSidebar?: boolean;
  steps: Array<string>;
  currentStep: number;
  children: ReactNode;
  showFooter?: boolean;
  showProgress?: boolean;
  dataTest?: string;
  transitionDirection?: 'top' | 'bottom';
  url?: string;
  renderNavigationItems?: () => ReactNode;
  withCalculator?: boolean;
  progressDisabled?: boolean;
  nextStepsDisabled?: boolean;
  layoutStyles?: string;
};

function Layout({
  steps,
  currentStep,
  children,
  showSidebar = true,
  showFooter = false,
  showProgress = true,
  dataTest = 'layout',
  transitionDirection,
  url,
  withCalculator = false,
  renderNavigationItems,
  progressDisabled = false,
  nextStepsDisabled = false,
  layoutStyles = '',
}: Props) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { offer, backupCurrentAmount, recoverPreviousAmount, isClientError } =
    useCalculator();

  const handleDrawerOpen = () => {
    backupCurrentAmount();
    setDrawerOpen(true);
  };

  const handleCancel = () => {
    recoverPreviousAmount();
    setDrawerOpen(false);
  };

  const handleConfirm = () => {
    setDrawerOpen(false);
  };

  const toggleDrawerOpen = () => {
    if (!drawerOpen) {
      handleDrawerOpen();
    } else {
      handleCancel();
    }
  };

  const showProgressOrCalculator = showProgress || withCalculator;

  return (
    <>
      {/* Wrapper */}
      <div
        data-test={dataTest}
        className={twMerge('w-full min-h-dvh flex flex-col', 'lg:flex-row')}
      >
        {/*

        SIDEBAR START

        */}
        {/* Mobile topbar or Desktop sidebar */}
        <aside
          className={twMerge(
            `flex flex-col gap-2 z-20 bg-background w-full shrink-0 ${!showSidebar && 'lg:hidden'}`,
            'lg:pt-11 lg:px-5 lg:pb-6 lg:relative lg:w-48 lg:border-r lg:gap-6',
          )}
        >
          {/* Desktop sidebar title */}
          <div className="hidden lg:block  border-b  pb-4 ">
            <div className="w-20 leading-5 font-medium text-lg">
              {t('common:loan_overview')}
            </div>
          </div>

          {/* Mobile logo, progress bar and help icon */}
          <div className="lg:hidden flex items-center justify-between  px-5 py-5 gap-5 border-b">
            <LogoSmall
              className="shrink-0"
              data-test="layout-sidebar-logo-mobile"
            />
            {showProgress && (
              <ProgressBar steps={steps.length} currentStep={currentStep} />
            )}
            <HelpIcon className="shrink-0" data-test="layout-help-icon" />
          </div>

          {showSidebar && (
            <div className="flex items-center justify-between pb-5 border-b lg:px-0 lg:w-full lg:border-b-0">
              {/* Desktop and mobile amount and term */}
              <SidebarAmountAndMTP
                amount={offer?.newPrincipal}
                agreementEndDate={offer?.agreementEndDate}
                totalInterest={offer?.totalInterest}
                interestRate={offer?.interestRate}
                dueDate={offer?.firstPaymentDate}
                annualPercentageRate={offer?.annualPercentageRate}
                drawerOpen={drawerOpen}
                isError={isClientError}
              />

              {/* Mobile chevron for opening calculator */}
              {withCalculator && (
                <MobileCalculalorChevron
                  onClick={toggleDrawerOpen}
                  active={drawerOpen}
                />
              )}
            </div>
          )}

          {/* Desktop button for opening calculator */}
          {withCalculator && (
            <DesktopCalculatorButton
              onClick={handleDrawerOpen}
              disabled={drawerOpen}
            />
          )}

          {/* Desktop footer inside sidebar */}
          {showFooter && (
            <footer className="hidden lg:block lg:mt-auto" data-test="footer">
              <div className="pb-3.5">
                <LogoFooter data-test="icon" />
              </div>
              <div
                className="text-muted text-xs"
                data-test="footer-information"
              >
                {t('common:footer_text')}
              </div>
            </footer>
          )}
        </aside>
        {/*

        SIDEBAR END

        */}

        {showProgressOrCalculator && (
          <div
            className={twMerge(
              'w-full flex flex-col relative h-full z-10',
              'lg:w-80 lg:border-r lg:px-11 lg:py-12',
            )}
          >
            {/* Drawer with calculator and offer details */}
            <Drawer
              drawerOpen={drawerOpen}
              handleCancel={handleCancel}
              handleConfirm={handleConfirm}
            />

            {/* Desktop progress */}
            <div className="hidden lg:flex lg:w-80 lg:h-full shrink-0">
              <div>
                <Logo className="mb-48" data-test="layout-sidebar-logo" />
                {showProgress && (
                  <Progress
                    steps={steps}
                    currentStep={currentStep}
                    disabled={Boolean(progressDisabled)}
                    nextStepsDisabled={Boolean(nextStepsDisabled)}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div
          className={classes(
            twMerge(
              'w-full max-w-3xl py-12 px-5 mx-auto flex-grow',
              'lg:py-40 lg:px-16 lg:ml-0',
              layoutStyles,
            ),
            drawerOpen ? styles.blur : styles['no-blur'],
          )}
        >
          {/* Desktop logo outside of the sidebar */}
          {!showProgressOrCalculator && showSidebar && (
            <nav className="hidden lg:flex lg:justify-end lg:absolute lg:top-12 lg:left-100">
              <Logo className="mb-48" data-test="layout-sidebar-logo" />
            </nav>
          )}

          {/* Desktop logout button */}
          <nav className="hidden lg:flex lg:justify-end lg:absolute lg:top-12 lg:right-20">
            {renderNavigationItems && renderNavigationItems()}
          </nav>

          <main>
            <div
              key={url}
              className={classes(
                'min-h-full',
                transitionDirection === 'top'
                  ? styles['top-fade-in']
                  : styles['bottom-fade-in'],
              )}
            >
              {children}
            </div>
          </main>
        </div>

        {/* Mobile footer */}
        {showFooter && (
          <footer
            className={classes(
              twMerge('bg-white border-t px-5 py-10', 'lg:hidden'),
              drawerOpen ? styles.blur : styles['no-blur'],
            )}
          >
            <div className="flex justify-between mb-3 items-center">
              <LogoFooter />
              {renderNavigationItems && renderNavigationItems()}
            </div>
            <div className="text-muted text-xs">{t('common:footer_text')}</div>
          </footer>
        )}
      </div>
    </>
  );
}

export default Layout;
